/**
 * Modbus Command Type catalog — super-admin CRUD over the MySQL
 * ``commandtype`` table. Listed under Tools → Modbus communication.
 *
 * Each row shows:
 *   - Parameter Address (Hex)
 *   - Description
 *   - Function (dropdown populated from existing ``valuetosend2``
 *     values across the whole table; admin can also type a new value)
 *
 * Backend: see ``commandtype_routes.py``.
 *   GET  /api/commandtype
 *   POST /api/commandtype
 *   PUT  /api/commandtype/{id}
 *   DEL  /api/commandtype/{id}
 */
import { useEffect, useMemo, useState } from "react";
import {
  Network, Plus, TrashSimple, FloppyDisk, X, MagnifyingGlass,
  ArrowsClockwise, WarningCircle, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";
import api from "@/lib/api";

const BLANK_ROW = { parameter_address: "", description: "", valuetosend2: "", driver_type: 0 };

export default function ModbusCommandTypesPage({ user, onLogout, onLogoutAll }) {
  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [drivers, setDrivers] = useState([]);     // iter150AW
  const [hasDriverColumn, setHasDriverColumn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  // ``edits[id]`` holds the in-flight patch for an existing row.
  // ``newRow`` is the staged row above the table.
  const [edits, setEdits] = useState({});
  const [newRow, setNewRow] = useState({ ...BLANK_ROW });
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Map driver_type code → human label (e.g. 1 → "EN600-EN500").
  const driverLabel = useMemo(() => {
    const m = new Map();
    drivers.forEach((d) => m.set(Number(d.code), d.label));
    return m;
  }, [drivers]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      // Parallel fetch — drivers list rarely fails even if commandtype
      // has issues, and vice-versa.
      const [c, d] = await Promise.all([
        api.get("/commandtype"),
        api.get("/driver-types").catch(() => ({ data: { items: [] } })),
      ]);
      setRows(c.data?.items || []);
      setFunctions(c.data?.functions || []);
      setHasDriverColumn(!!c.data?.has_driver_column);
      setDrivers(d.data?.items || []);
      setEdits({});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load command types");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const drv = driverLabel.get(Number(r.driver_type)) || "";
      return (
        (r.parameter_address || "").toLowerCase().includes(q)
        || (r.description || "").toLowerCase().includes(q)
        || (r.valuetosend2 || "").toLowerCase().includes(q)
        || drv.toLowerCase().includes(q)
        || String(r.driver_type || "").includes(q)
      );
    });
  }, [rows, search, driverLabel]);

  // ── Edit handlers ─────────────────────────────────────────────
  const editField = (id, field, value) => {
    setEdits((cur) => ({
      ...cur,
      [id]: { ...(cur[id] || {}), [field]: value },
    }));
  };
  const merged = (row) => ({ ...row, ...(edits[row.id] || {}) });
  const isDirty = (row) => {
    const patch = edits[row.id];
    if (!patch) return false;
    return Object.entries(patch).some(([k, v]) => (row[k] || "") !== v);
  };

  const saveRow = async (row) => {
    const payload = merged(row);
    if (!payload.parameter_address.trim()) {
      toast.error("Parameter Address is required");
      return;
    }
    setSavingId(row.id);
    try {
      await api.put(`/commandtype/${encodeURIComponent(row.id)}`, {
        parameter_address: payload.parameter_address.trim(),
        description: payload.description.trim(),
        valuetosend2: payload.valuetosend2.trim(),
        driver_type: Number(payload.driver_type || 0),
      });
      toast.success(`Saved ${payload.parameter_address}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete command type ${row.parameter_address || row.id}?`)) return;
    setDeletingId(row.id);
    try {
      await api.delete(`/commandtype/${encodeURIComponent(row.id)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const createRow = async () => {
    const payload = {
      parameter_address: newRow.parameter_address.trim(),
      description: newRow.description.trim(),
      valuetosend2: newRow.valuetosend2.trim(),
      driver_type: Number(newRow.driver_type || 0),
    };
    if (!payload.parameter_address) {
      toast.error("Parameter Address is required");
      return;
    }
    setSavingId("__new__");
    try {
      await api.post("/commandtype", payload);
      toast.success(`Added ${payload.parameter_address}`);
      setNewRow({ ...BLANK_ROW });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminPageHeader
        icon={<Network weight="fill" />}
        title="Modbus command types"
        subtitle="commandtype table — parameter addresses and their Modbus functions"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <div className="mx-auto max-w-[1200px] px-3 py-3 space-y-3">

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-[420px]">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              data-testid="commandtype-search"
              placeholder="Search address, description, function…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-2 h-8 rounded-sm border border-slate-300 text-[12px]"
            />
          </div>
          <button
            data-testid="commandtype-refresh"
            onClick={load}
            disabled={loading}
            className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 disabled:opacity-50">
            <ArrowsClockwise size={11} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
          <div className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider font-bold">
            {filteredRows.length}{search ? ` of ${rows.length}` : ""} rows · {functions.length} functions
          </div>
        </div>

        {/* Error */}
        {err && (
          <div data-testid="commandtype-error"
               className="px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-[12px] text-red-800 inline-flex items-center gap-2">
            <WarningCircle size={14} weight="bold" /> {err}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="commandtype-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {hasDriverColumn && (
                  <th className="px-3 py-2 text-left font-bold w-[180px]">Driver type</th>
                )}
                <th className="px-3 py-2 text-left font-bold w-[200px]">Modbus Address (Hex)</th>
                <th className="px-3 py-2 text-left font-bold">Command Description</th>
                <th className="px-3 py-2 text-left font-bold w-[260px]">Function</th>
                <th className="px-3 py-2 text-right font-bold w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* New row */}
              <tr className="bg-indigo-50/40 border-b border-indigo-100"
                  data-testid="commandtype-new-row">
                {hasDriverColumn && (
                  <td className="px-3 py-2">
                    <DriverPicker
                      value={newRow.driver_type}
                      options={drivers}
                      onChange={(v) => setNewRow({ ...newRow, driver_type: v })}
                      testId="commandtype-new-driver"
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <input
                    type="text"
                    data-testid="commandtype-new-address"
                    placeholder="0x0400"
                    value={newRow.parameter_address}
                    onChange={(e) => setNewRow({ ...newRow, parameter_address: e.target.value })}
                    className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px] font-mono"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    data-testid="commandtype-new-desc"
                    placeholder="What this command does"
                    value={newRow.description}
                    onChange={(e) => setNewRow({ ...newRow, description: e.target.value })}
                    className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px]"
                  />
                </td>
                <td className="px-3 py-2">
                  <FunctionPicker
                    value={newRow.valuetosend2}
                    options={functions}
                    onChange={(v) => setNewRow({ ...newRow, valuetosend2: v })}
                    testId="commandtype-new-function"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid="commandtype-add-btn"
                    onClick={createRow}
                    disabled={savingId === "__new__" || !newRow.parameter_address.trim()}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-indigo-700 hover:bg-indigo-800 text-white disabled:opacity-50">
                    <Plus size={11} weight="bold" />
                    {savingId === "__new__" ? "Adding…" : "Add"}
                  </button>
                </td>
              </tr>

              {/* Existing rows */}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={hasDriverColumn ? 5 : 4} className="px-3 py-6 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={hasDriverColumn ? 5 : 4} className="px-3 py-6 text-center text-slate-500">
                    {search ? "No rows match your search." : "Table is empty — add the first row above."}
                  </td>
                </tr>
              )}
              {filteredRows.map((row) => {
                const m = merged(row);
                const dirty = isDirty(row);
                return (
                  <tr key={row.id}
                      data-testid={`commandtype-row-${row.id}`}
                      className={`border-b border-slate-100 ${dirty ? "bg-amber-50/40" : "hover:bg-slate-50"}`}>
                    {hasDriverColumn && (
                      <td className="px-3 py-1.5">
                        <DriverPicker
                          value={m.driver_type}
                          options={drivers}
                          onChange={(v) => editField(row.id, "driver_type", v)}
                          testId={`commandtype-driver-${row.id}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        data-testid={`commandtype-address-${row.id}`}
                        value={m.parameter_address}
                        onChange={(e) => editField(row.id, "parameter_address", e.target.value)}
                        className="w-full px-2 py-1 rounded-sm border border-slate-200 text-[12px] font-mono"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        data-testid={`commandtype-desc-${row.id}`}
                        value={m.description}
                        onChange={(e) => editField(row.id, "description", e.target.value)}
                        className="w-full px-2 py-1 rounded-sm border border-slate-200 text-[12px]"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <FunctionPicker
                        value={m.valuetosend2}
                        options={functions}
                        onChange={(v) => editField(row.id, "valuetosend2", v)}
                        testId={`commandtype-function-${row.id}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      <button
                        data-testid={`commandtype-save-${row.id}`}
                        onClick={() => saveRow(row)}
                        disabled={!dirty || savingId === row.id}
                        title={dirty ? "Save changes" : "No changes"}
                        className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm mr-1 ${
                          dirty
                            ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}>
                        {savingId === row.id ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <FloppyDisk size={11} weight="bold" />}
                        {dirty && <span>Save</span>}
                      </button>
                      <button
                        data-testid={`commandtype-delete-${row.id}`}
                        onClick={() => deleteRow(row)}
                        disabled={deletingId === row.id}
                        title="Delete row"
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-50">
                        {deletingId === row.id ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <TrashSimple size={11} weight="bold" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Functions inline reference */}
        {functions.length > 0 && (
          <div className="text-[10.5px] text-slate-500 px-1">
            <span className="font-bold uppercase tracking-wider text-slate-600 mr-2">Existing functions:</span>
            {functions.map((f, i) => (
              <span key={f} className="font-mono text-slate-700">
                {f}{i < functions.length - 1 ? " · " : ""}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Function dropdown: shows every existing ``valuetosend2`` value in
 * the catalog plus a "Custom…" option that flips to a plain text
 * input. Keeps the operator on a short list while still allowing new
 * values when the catalog needs to grow.
 */
function FunctionPicker({ value, options, onChange, testId }) {
  const known = options.includes(value);
  const [custom, setCustom] = useState(!known && !!value);

  if (custom) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          data-testid={`${testId}-input`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom function name"
          className="flex-1 px-2 py-1 rounded-sm border border-slate-300 text-[12px] font-mono"
        />
        <button
          type="button"
          onClick={() => { setCustom(false); onChange(""); }}
          title="Back to dropdown"
          className="p-1 rounded-sm text-slate-500 hover:bg-slate-100">
          <X size={11} weight="bold" />
        </button>
      </div>
    );
  }
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustom(true);
          onChange("");
        } else {
          onChange(e.target.value);
        }
      }}
      className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px] font-mono bg-white">
      <option value="">— select —</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
      {value && !options.includes(value) && (
        <option value={value}>{value} (current)</option>
      )}
      <option value="__custom__">+ Custom…</option>
    </select>
  );
}

/**
 * Driver type dropdown — populated from the /api/driver-types table.
 * Shows ``label  ·  code`` so the operator sees both. Falls back to
 * just the code when the catalog doesn't have a label for that value.
 */
function DriverPicker({ value, options, onChange, testId }) {
  const v = Number(value || 0);
  const known = options.some((d) => Number(d.code) === v);
  return (
    <select
      data-testid={testId}
      value={String(v)}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px] bg-white">
      <option value="0">— none —</option>
      {options.map((d) => (
        <option key={d.code} value={String(d.code)}>
          {d.label} · {d.code}
        </option>
      ))}
      {v > 0 && !known && (
        <option value={String(v)}>{v} (unknown — add it to the driver list)</option>
      )}
    </select>
  );
}
